import { TeamSelectorStyled } from './Navs/SectionSideBar.styled';
import {
  DefaultInputStyled,
  InputStyled,
  LabelStyled,
} from '@OnArk/styles/Form.styled';
import { SubTitleStyled } from '@OnArk/styles/Text.styled';

import React from 'react';

import Link from 'next/link';

import Form, { ErrorMsgs, useForm } from '@hangar31/rc-form';
import useModal from '@hangar31/rc-modal';
import { findDelete } from '@hangar31/rc-utils';
import { useSection } from '@OnArk/contexts/section';
import Icon from '@OnArk/SVGs/Icon';
import posthog from '@OnArk/utils/posthog';

import SectionSettingsButton from '../Shared/SectionSettingsButton';

import NewTeamInput from './NewTeamInput';

/** The fields of the ministry-linking form below (`useForm()` is untyped by
 *  default; this mirrors its `defaultData`). */
interface MinistryLinkFields {
  id: string;
  ministries?: { id: string }[];
}

function Ministries() {
  const { teams } = useSection();
  const { fields, setField } = useForm<MinistryLinkFields>();

  return (
    <>
      {teams?.map(team => (
        <LabelStyled key={team.id} css={{ margin: 0 }}>
          <DefaultInputStyled
            checked={!!fields.ministries?.find(m => m.id === team.id)}
            id={fields.id + team.id}
            type="checkbox"
            onChange={e => {
              const teams = fields.ministries ?? [];

              if (e.target.checked) {
                setField('ministries', [...teams, { id: team.id }]);
              } else {
                setField(
                  'ministries',
                  teams.filter(({ id }) => id !== team.id)
                );
              }
            }}
          />
          <label htmlFor={fields.id + team.id}>
            <span>{team.name}</span>
          </label>
        </LabelStyled>
      ))}
    </>
  );
}

/** Per-gift enable/disable checklist for the Spiritual Gifts test. Checked =
 *  enabled; unchecking adds the gift NAME to `settings.disabledGifts` (see
 *  SectionSettings). Rendered inside the main settings Form so it autosaves
 *  through the same `updateSection` path as the test-type select. */
function GiftToggles({ gifts }: { gifts: { id: string; name: string }[] }) {
  const { fields, setField } = useForm<{
    settings?: { disabledGifts?: string[] };
  }>();
  const disabled = fields.settings?.disabledGifts ?? [];
  const enabledCount = gifts.filter(g => !disabled.includes(g.name)).length;

  return (
    <>
      <p>
        <strong>Gifts To Include: </strong>
        <br />
        <small css={{ fontWeight: 400, textTransform: 'none' }}>
          (Unchecked gifts aren&apos;t asked about or shown in results)
        </small>
      </p>
      {gifts.map(gift => (
        <LabelStyled key={gift.id} css={{ margin: 0 }}>
          <DefaultInputStyled
            checked={!disabled.includes(gift.name)}
            id={`gift${gift.id}`}
            type="checkbox"
            onChange={e => {
              if (e.target.checked) {
                setField(
                  'settings.disabledGifts',
                  disabled.filter(name => name !== gift.name)
                );
              } else {
                setField('settings.disabledGifts', [...disabled, gift.name]);
              }
            }}
          />
          <label htmlFor={`gift${gift.id}`}>
            <span>{gift.name}</span>
          </label>
        </LabelStyled>
      ))}
      {enabledCount < 3 && (
        <p
          css={theme => ({
            color: theme.colors.grayDark,
            fontSize: '0.85rem',
            lineHeight: 1.55,
          })}
        >
          Fewer than 3 gifts are enabled — results will show only the ones that
          remain.
        </p>
      )}
    </>
  );
}

function TestSettingsForm() {
  const { client, currentSection, setChanges, setSettings, types } =
    useSection();
  const names = {
    DISC: 'DISC Test',
    ENNEAGRAM: 'Enneagram Test',
    SPIRITUAL_GIFTS: 'Spiritual Gifts Test',
  };

  // Only show ministry-linker cards once a test is actually picked.
  // Without this gate, a `.filter(t => t.testType === undefined)` matches
  // every type whose testType happens to be null, dumping a wall of
  // unrelated result categories into the modal the moment it opens.
  // `settings` is the GraphQL Json scalar (typed `any`); narrow to the test
  // shape this form edits.
  const selectedType = (
    currentSection.settings as { type?: TestType } | null | undefined
  )?.type;
  const testTypes = selectedType
    ? types?.filter(t => t.testType === selectedType)
    : [];

  return (
    <div
      css={theme => ({
        background: theme.colors.white,
        minHeight: '100%',
        padding: '1.5rem',
      })}
    >
      <Form
        autoSave={50}
        defaultData={currentSection}
        onSubmit={async fields => {
          // `settings` is the GraphQL Json scalar (typed `any`); narrow to the
          // test shape this form edits. Send the FULL settings object (not just
          // the changed keys): `settings` is one JSONB blob, so toggling a gift
          // must not drop `type`, and vice-versa.
          const settings = (fields.settings ?? {}) as {
            type?: TestType;
            disabledGifts?: string[];
          };

          await client.updateSection({
            data: {
              name: settings.type ? names[settings.type] : undefined,
              settings,
            },
            id: currentSection.id,
          });
          setSettings(settings);
          setChanges(c => c + 1);
          posthog?.capture('setTestType', { type: settings.type });
        }}
      >
        <SubTitleStyled
          css={{
            alignItems: 'center',
            display: 'flex',
            marginTop: '0',
            width: '100%',
          }}
        >
          Test Settings
        </SubTitleStyled>
        <LabelStyled>
          <span>
            <Icon
              css={{ marginRight: '0.8rem' }}
              name="SECTIONS_Test"
              size="25"
            />
            Test Type
          </span>
          <InputStyled required elementType="select" name="settings.type">
            <option disabled value="">
              Select a Type
            </option>
            <option value="ENNEAGRAM">Enneagram</option>
            <option value="SPIRITUAL_GIFTS">Spiritual Gifts</option>
            <option value="DISC">DISC</option>
          </InputStyled>
        </LabelStyled>
        <ErrorMsgs />
        {selectedType === 'SPIRITUAL_GIFTS' && !!testTypes?.length && (
          <GiftToggles gifts={testTypes} />
        )}
      </Form>
      {selectedType && (
        <>
          <p>
            <Link
              href={`/admin/test-results?type=${selectedType}`}
              target="_blank"
            >
              Preview &amp; print the result descriptions guests will see
            </Link>
          </p>
          <p>
            <strong>Suggest These Ministries: </strong>
            <br />
            <small css={{ fontWeight: 400, textTransform: 'none' }}>
              (View/Edit all your ministries and their descriptions on the{' '}
              <Link href="/admin/ministries">Ministries Page</Link>)
            </small>
          </p>
          <NewTeamInput />
        </>
      )}
      {testTypes?.map(type => (
        <TeamSelectorStyled key={type.id}>
          <Form
            autoSave={500}
            defaultData={{
              id: type.id,
              ministries: type.ministries ?? [],
            }}
            onSubmit={async (info, initial) => {
              const init = initial.ministries || type.ministries || [];
              const ministries = {
                connect: [] as { id: string }[],
                disconnect: [] as { id: string }[],
              };

              if (info.ministries) {
                ministries.connect = info.ministries.filter(
                  m => !init.find(i => i.id === m.id)
                );
                ministries.disconnect = findDelete(info.ministries, init, 'id');
              }
              const { data } = await client.updateLinkMinistries({
                data: { ministries },
                id: info.id,
              });

              posthog?.capture('saveMinstriesForTest');

              return { ...data };
            }}
          >
            <h1>{type.name}</h1>
            <Ministries />
          </Form>
        </TeamSelectorStyled>
      ))}
    </div>
  );
}

export default function TestSettings() {
  const { currentSection } = useSection();
  // `settings` is the GraphQL Json scalar (typed `any`); narrow to what we
  // read.
  const needsAttention = !(
    currentSection.settings as { type?: TestType } | null | undefined
  )?.type;
  const [Modal] = useModal({
    ButtonComponent: SectionSettingsButton,
    buttonProps: {
      'data-tour': 'test-pick-button',
      label: needsAttention ? 'Pick a test' : 'Settings',
      needsAttention,
    },
    maxHeight: 600,
    maxWidth: 700,
    ModalComponent: TestSettingsForm,
    ms: 300,
    transitionOpacity: true,
  });

  return <Modal />;
}
